import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PackageType, ScopeableType } from '../../../types';
import { ActiveStatus } from '../../../utils/contract-utils';
import {
  DiscountType,
  getEditDefaultValues,
  IDiscount,
} from '../../../utils/discount-utils';
import { ScopeSelector } from '../../form-fields/scope-selector';
import { DiscountDatePickers } from './components/date-pickers';
import { DiscountInput } from './components/discount-input';
import { DiscountTypeSelect } from './components/discount-type-select';
import { SuppressEmptyChargesCheckbox } from './components/suppress-empty-charges';
import {
  DiscountFormValues,
  DISCOUNT_CANCEL_BUTTON_TEST_ID,
  DISCOUNT_SUBMIT_BUTTON_TEST_ID,
  ParsedDiscountValues,
} from './form-constants';
import { DiscountValidationSchema } from './validation-schema';

export interface DiscountFormProps {
  clientId: number;
  formTitle: string;
  onSubmitCallback: (values: ParsedDiscountValues) => void | Promise<void>;
  onCancelCallback: () => void;
  submitTitle?: string | null;
  discountToEdit: IDiscount | null;
  currentDiscounts?: IDiscount[];
  availablePackages: PackageType[];
  defaultType?: DiscountType;
}

export const DiscountForm = ({
  clientId,
  formTitle,
  onSubmitCallback,
  onCancelCallback,
  submitTitle,
  discountToEdit,
  currentDiscounts,
  availablePackages,
  defaultType,
}: DiscountFormProps): JSX.Element => {
  const defaultValues: DiscountFormValues = {
    clientId,
    discountType: defaultType ?? DiscountType.billingCaps,
    startDate: dayjs().toDate(),
    endDate: null,
    discount: '',
    suppressEmptyCharges: true,
    scopeableType: ScopeableType.Global,
    scopes: [],
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    context: { currentDiscounts, discountToEdit },
    resolver: yupResolver(DiscountValidationSchema),
    criteriaMode: 'all',
  });

  const { handleSubmit, formState, reset } = methods;
  const { isValid, errors, isSubmitting, isDirty, isSubmitted } = formState;

  // Scopes can only be edited before a discount takes effect. Once it is
  // active or past, the scopes are locked in alongside the type, amount, and
  // start date.
  const scopesDisabled = discountToEdit
    ? discountToEdit.status !== ActiveStatus.Future
    : false;

  useEffect(() => {
    if (discountToEdit) {
      reset(getEditDefaultValues(discountToEdit));
    }
  }, [discountToEdit, reset]);

  const parseFormValues = (data: DiscountFormValues): ParsedDiscountValues => ({
    // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
    clientId: data.clientId,
    discount:
      typeof data.discount === 'string'
        ? parseInt(data.discount)
        : data.discount,
    discountType: data.discountType,
    endDate: data.endDate ? dayjs(data.endDate).format('YYYY-MM-DD') : null,
    startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
    suppressEmptyCharges: data.suppressEmptyCharges,
    scopes:
      data.scopeableType === ScopeableType.Global
        ? [{ scopeableType: ScopeableType.Global, scopeableId: null }]
        : data.scopes,
  });

  const onFormSubmit = async (data: DiscountFormValues): Promise<void> => {
    await onSubmitCallback(parseFormValues(data));
  };

  return (
    <FormProvider {...methods}>
      <form>
        <HHStack spacing={6}>
          <HHTypography hhVariant="section-title">{formTitle}</HHTypography>
          <HHStack display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={6}>
            <DiscountTypeSelect discountToEdit={discountToEdit} />
            <HHStack sx={{ gridColumn: 'span 2' }}>
              <ScopeSelector
                availablePackages={availablePackages}
                disabled={scopesDisabled}
              />
            </HHStack>
            <DiscountDatePickers discountToEdit={discountToEdit} />
            <DiscountInput discountToEdit={discountToEdit} />
            <SuppressEmptyChargesCheckbox />
          </HHStack>
          <HHStack direction="row" spacing={6} alignSelf="end">
            <HHButton
              data-testid={DISCOUNT_CANCEL_BUTTON_TEST_ID}
              hhVariant="variant-bypass"
              variant="outlined"
              size="medium"
              color="primary"
              onClick={(): void => {
                reset(defaultValues);
                onCancelCallback();
              }}
            >
              Cancel
            </HHButton>
            <HHButton
              data-testid={DISCOUNT_SUBMIT_BUTTON_TEST_ID}
              hhVariant="variant-bypass"
              variant="contained"
              size="medium"
              color="primary"
              disabled={
                !isDirty || (errors && !isValid) || isSubmitting || isSubmitted
              }
              onClick={handleSubmit(onFormSubmit)}
            >
              {submitTitle}
            </HHButton>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
